import { GeoJSONReader, GeoJSONWriter } from "jsts/org/locationtech/jts/io";
import { BufferOp } from "jsts/org/locationtech/jts/operation/buffer";
import { BufferParameters } from "jsts/org/locationtech/jts/operation/buffer";
import { GeometryFactory } from "jsts/org/locationtech/jts/geom";
import proj4 from "proj4";
import type { Feature, LineString, Polygon, Position } from "geojson";

proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");

const UTM_ZONE_WIDTH = 6;
const UTM_ZONE_OFFSET = 180;
const NORTH_EPSG_PREFIX = "326";
const SOUTH_EPSG_PREFIX = "327";

export interface AdvancedBufferOptions {
	distance: number;
	units?: "meters" | "kilometers";
	quadrantSegments?: number;
	endCapStyle?: "round" | "flat" | "square";
	joinStyle?: "round" | "mitre" | "bevel";
	mitreLimit?: number;
}

// Works exactly the same as Turf's buffer function but allows for additional options like end cap style and join style
export function advancedBuffer(
	feature: Feature,
	options: AdvancedBufferOptions,
): Feature<Polygon> {
	const {
		distance,
		units = "meters",
		quadrantSegments = 8,
		endCapStyle = "round",
		joinStyle = "round",
		mitreLimit = 10,
	} = options;

	const firstPoint = (feature.geometry as LineString)
		.coordinates[0] as Position;
	const [long, lat] = firstPoint;
	const tempProjEPSG = getUtmEpsgCode(long, lat);

	// Ensure proj4 has the definition for this dynamic zone before using it
	if (!proj4.defs(tempProjEPSG)) {
		const zone = Math.floor((long + UTM_ZONE_OFFSET) / UTM_ZONE_WIDTH) + 1;
		proj4.defs(
			tempProjEPSG,
			`+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs`,
		);
	}

	const distanceInMeters =
		units === "kilometers" ? distance * 1000 : distance;

	if (!proj4.defs("EPSG:4326") || !proj4.defs(tempProjEPSG)) {
		throw new Error(
			`Coordinate systems not defined. Missing EPSG:4326 or ${tempProjEPSG}`,
		);
	}

	const projectedFeature = {
		...feature,
		geometry: {
			...feature.geometry,
			coordinates: projectCoordinates(
				(feature.geometry as LineString | Polygon).coordinates,
				"EPSG:4326",
				tempProjEPSG,
			),
		},
	};

	const geometryFactory = new GeometryFactory();
	const reader = new GeoJSONReader(geometryFactory);
	let geometry;
	try {
		geometry = reader.read(projectedFeature.geometry);
	} catch (error) {
		throw new Error(
			`Invalid geometry for buffering: ${error instanceof Error ? error.message : error}`,
		);
	}

	const bufferParameters = new BufferParameters(
		quadrantSegments,
		getEndCapValue(endCapStyle),
		getJoinValue(joinStyle),
		mitreLimit,
	);

	const bufferOp = new BufferOp(geometry, bufferParameters);
	const bufferedGeometry = bufferOp.getResultGeometry(distanceInMeters);

	const writer = new GeoJSONWriter();
	const bufferedGeoJSON = writer.write(bufferedGeometry);

	const finalCoordinates = projectCoordinates(
		bufferedGeoJSON.coordinates,
		tempProjEPSG,
		"EPSG:4326",
	);

	return {
		type: "Feature",
		properties: feature.properties || {},
		geometry: {
			type: "Polygon",
			coordinates: finalCoordinates as Position[][],
		},
	};
}

function getUtmEpsgCode(longitude: number, latitude: number): string {
	const zone = Math.floor((longitude + UTM_ZONE_OFFSET) / UTM_ZONE_WIDTH) + 1;
	const epsgPrefix = latitude >= 0 ? NORTH_EPSG_PREFIX : SOUTH_EPSG_PREFIX;
	const epsgCode = `${epsgPrefix}${zone.toString().padStart(2, "0")}`;
	return `EPSG:${epsgCode}`;
}

function projectCoordinates<T extends Position[] | Position[][]>(
	coordinates: T,
	inProj: string,
	outProj: string,
): T {
	if (coordinates.length > 0 && typeof coordinates[0][0] === "number") {
		return (coordinates as Position[]).map(coord =>
			proj4(inProj, outProj, coord),
		) as T;
	}

	return (coordinates as Position[][]).map(ring =>
		ring.map(coord => proj4(inProj, outProj, coord)),
	) as T;
}

function getEndCapValue(style: string): number {
	switch (style) {
		case "flat":
			return BufferParameters.CAP_FLAT;
		case "square":
			return BufferParameters.CAP_SQUARE;
		case "round":
		default:
			return BufferParameters.CAP_ROUND;
	}
}

function getJoinValue(style: string): number {
	switch (style) {
		case "mitre":
			return BufferParameters.JOIN_MITRE;
		case "bevel":
			return BufferParameters.JOIN_BEVEL;
		case "round":
		default:
			return BufferParameters.JOIN_ROUND;
	}
}